"use client";

import React from "react";
import { BriefcaseBusiness, CircleCheck, FileSpreadsheet, Handshake, Landmark, MonitorCog } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const opportunityAreas = [{
  title: "Accounting and Bookkeeping",
  description: "Support financial records, reconciliations, reporting, transaction review, and practical file organization.",
  icon: FileSpreadsheet
}, {
  title: "Program and Project Management",
  description: "Help clients organize work plans, track progress, manage deadlines, and move important projects forward.",
  icon: BriefcaseBusiness
}, {
  title: "Management Consulting",
  description: "Support planning, problem-solving, reporting, process improvement, and practical advisory work for client organizations.",
  icon: Handshake
}, {
  title: "Year-End and Audit Preparation",
  description: "Assist with year-end files, audit readiness, financial schedules, supporting documentation, and reporting follow-up.",
  icon: CircleCheck
}, {
  title: "Microsoft 365 and Reporting Systems",
  description: "Help build tools using Microsoft 365, SharePoint, Excel, Power BI, macros, and workflow systems.",
  icon: MonitorCog
}, {
  title: "Economic Development Support",
  description: "Contribute to work that helps communities and organizations plan, manage, and advance economic development opportunities.",
  icon: Landmark
}];
export default function TeamSection() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262107" id="interest-sections-part-1" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-14 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 text-secondary bg-secondary/10 border-transparent">
            Areas of Opportunity
          </Badge>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary-500 mb-6">
            Skills we are often looking for
          </h2>

          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            RiverBend’s work brings together financial management, reporting,
            project support, systems development, and practical advisory work.
            Even when a specific job is not posted, we are often interested in
            hearing from people with skills that can support RiverBend, our
            clients, and the communities and organizations we serve.
          </p>
        </div>

        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 ease-out delay-200 ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {opportunityAreas.map((area, i) => {
          const Icon = area.icon;
          return <Card key={area.title} data-index={i} className="h-full bg-card border border-border shadow-sm rounded-lg">
                <CardContent className="p-7 md:p-8">
                  <div className="size-12 rounded-md bg-secondary/10 flex items-center justify-center mb-5">
                    <Icon className="size-6 text-secondary" />
                  </div>

                  <h3 className="text-xl font-default font-bold text-primary-500 mb-3">
                    {area.title}
                  </h3>

                  <p className="text-muted-foreground font-default leading-relaxed">
                    {area.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="mt-10 bg-secondary/10 border border-secondary/20 rounded-md p-6 md:p-8">
          <p className="text-muted-foreground font-default leading-relaxed text-base md:text-lg">
            We are looking for people who care about doing good work, solving
            real problems, building strong relationships, and helping First
            Nation communities, Northern organizations, and economic development
            opportunities succeed.
          </p>
        </div>
      </div>
    </section>;
}